import { useState } from "react";
import { Link } from "react-router-dom";
import { LucideBookOpen, LucideChevronRight, LucideChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getTypeLabelByValue } from "@/constants/reportTypes";

const PAGE_SIZE = 10;

function MetaRow({ item, extraMetadata }) {
  const parts = [
    item.name || null,
    item.reportType ? getTypeLabelByValue(item.reportType) : null,
    ...(extraMetadata?.(item) ?? []),
  ].filter(Boolean);

  if (parts.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-2 mt-0.5">
      {parts.map((label, i) => (
        <span key={i} className="contents">
          {i > 0 && <span className="text-muted-foreground/30 text-xs">·</span>}
          <span className="text-xs text-muted-foreground">{label}</span>
        </span>
      ))}
    </div>
  );
}

export default function ClassList({
  items,
  loading,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyMessage,
  emptyAction,
  getLinkPath,
  renderNameExtra,
  renderAction,
  extraMetadata,
}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-24 border border-dashed border-border rounded-xl text-muted-foreground">
        {EmptyIcon && <EmptyIcon size={44} className="mx-auto mb-4 opacity-25" />}
        <p className="font-medium mb-1">{emptyTitle}</p>
        <p className={`text-sm opacity-70${emptyAction ? " mb-5" : ""}`}>
          {emptyMessage}
        </p>
        {emptyAction}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {pageItems.map((item) => (
        <div
          key={item._id}
          className="group border border-border rounded-lg bg-card hover:border-primary/40 hover:shadow-sm transition-all duration-150 flex items-center"
        >
          <Link
            to={getLinkPath(item)}
            className="flex-1 flex items-center gap-3 p-4 min-w-0"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <LucideBookOpen size={15} className="text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-foreground leading-tight truncate">
                  {[
                    item.grade ? `Grade ${item.grade}` : null,
                    item.schoolYear || null,
                  ].filter(Boolean).join(' · ') || item.name || 'Unnamed class'}
                </p>
                {renderNameExtra?.(item)}
              </div>
              <MetaRow item={item} extraMetadata={extraMetadata} />
            </div>
            <LucideChevronRight
              size={15}
              className="text-muted-foreground/30 group-hover:text-muted-foreground transition-colors shrink-0"
            />
          </Link>
          <div className="pr-3">{renderAction(item)}</div>
        </div>
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            className="gap-1"
          >
            <LucideChevronLeft size={14} />
            Prev
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages - 1}
            className="gap-1"
          >
            Next
            <LucideChevronRight size={14} />
          </Button>
        </div>
      )}
    </div>
  );
}
